import { http } from 'src/utils/http.util'
import {
  TPatientListRequest,
  TPatientListResponse,
} from 'src/pages/medical/patients/_components/patients.types'
import {
  TPatientReview,
  TPatientReviewExportRequest,
  TPatientReviewList,
  TPatientReviewListRequest,
  TPatientReviewStatsResponse,
} from 'src/pages/medical/reviews/_components/reviews.types'
import { TPatientForSearchBar } from 'src/components/searchbar/searchbar.type'
import { TPatient } from 'src/types/patient'
import { TPatientInteropLog } from 'src/pages/patients/patient-file/_components/header/interop-logs/interop-log.type'
import {
  TInteropMapping,
  TTestInteropMappingResponse,
} from 'src/pages/patients/patient-file/_components/header/interop-logs/interop-mapping.type'
import { TMedicalTeamComment } from 'src/components/medical-team-comments/medical-team-comments.type'
import { TResourceChangeLog, TResourceChangeLogFilters } from 'src/types/resource-change-log'

export const PatientService = {
  getPatientDebugData: async (projectId: number, patientId: number): Promise<unknown> => {
    return http.get(`/projects/${projectId}/patients/${patientId}/debug-data`).then((res) => res.data)
  },
  /**
   * Cette fonction correspond à l'ancienne page de retour patient
   * @deprecated
   * @param projectId
   * @returns
   */
  dreprecatedGetProjectReviews: async (projectId: number): Promise<any[]> => {
    return http.get(`/projects/${projectId}/reviews`).then((res) => res.data)
  },
  getPatientListByTab: async (
    projectId: number,
    data: TPatientListRequest,
  ): Promise<TPatientListResponse> => {
    return http.post(`/projects/${projectId}/patients-list/${data.tabId}`, data.body).then((res) => res.data)
  },
  getPatientReviewList: async (
    projectId: number,
    data: TPatientReviewListRequest,
  ): Promise<TPatientReviewList> => {
    return http.post(`/projects/${projectId}/patient-reviews/list`, data).then((res) => res.data)
  },
  getPatientReviewListByDate: async (projectId: number, date: string): Promise<Array<TPatientReview>> => {
    return http.get(`/projects/${projectId}/patient-reviews/list/${date}`).then((res) => res.data)
  },
  exportPatientReviews: async (
    projectId: number,
    payload: TPatientReviewExportRequest,
  ): Promise<Array<TPatientReview>> => {
    return http.post(`/projects/${projectId}/patient-reviews/export`, payload).then((res) => res.data)
  },
  getPatientReviewStats: async (projectId: number): Promise<TPatientReviewStatsResponse> => {
    return http.get(`/projects/${projectId}/patient-reviews/stats`).then((res) => res.data)
  },
  getExternalPatientByRemoteId: async (projectId: number, remoteId: string): Promise<TPatient> => {
    return http.get(`/projects/${projectId}/patients/external/${remoteId}`).then((res) => res.data)
  },
  getSearchbarPatientList: async (projectId: number): Promise<Array<TPatientForSearchBar>> => {
    try {
      const result = await http.get(`/projects/${projectId}/patients-identities`)
      return result.data
    } catch (error: any) {
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.requiredSpecialAuthorization === 'CAN_VIEW_PATIENT_IDENTITIES'
      ) {
        return []
      } else {
        throw error
      }
    }
  },
  searchPatient: async (projectId: number, search: string): Promise<Array<TPatientForSearchBar>> => {
    return http.get(`/projects/${projectId}/patients/search?s=${search}`).then((res) => res.data)
  },
  getPatientInteropLogsData: (projectId: number, patientId: number): Promise<TPatientInteropLog[]> => {
    return http.get(`/projects/${projectId}/patients/${patientId}/interop`).then((res) => res.data)
  },
  getPatientAnonymizedInteropLogsData: (projectId: number, patientId: number): Promise<unknown[]> => {
    return http
      .get(`/projects/${projectId}/patients/${patientId}/interop/anonymized-history`)
      .then((res) => res.data)
  },
  testInteropLogMapping: (
    projectId: number,
    patientId: number,
    interopLogId: number,
  ): Promise<TTestInteropMappingResponse> => {
    return http
      .get(`/projects/${projectId}/patients/${patientId}/interop/${interopLogId}/test-mapping`)
      .then((res) => res.data)
  },
  replayInteropLogMapping: (interopLogId: number): Promise<TInteropMapping> => {
    return http.post('/interop/replay', { interopLogIdList: [interopLogId] }).then((res) => res.data)
  },
  postSMS: (projectId: number, patientId: number, data: any) => {
    return http.post(`/projects/${projectId}/patients/${patientId}/sms`, data)
  },
  getSmsDocument: (projectId: number, patientId: number, smsLogId: number) => {
    return http.get(`/projects/${projectId}/patients/${patientId}/sms/${smsLogId}/document`, {
      responseType: 'blob',
    })
  },
  getSMS: (
    projectId: number,
    patientId: number,
    offset = -1,
    limit = -1,
    filterByPatientFollowupId?: number,
  ) => {
    let params = '?sort=DESC'
    if (offset >= 0) {
      params += `&offset=${offset}`
    }
    if (limit >= 0) {
      params += `&limit=${limit}`
    }
    if (filterByPatientFollowupId) {
      params += `&filterByPatientFollowupId=${filterByPatientFollowupId}`
    }
    return http.get(`/projects/${projectId}/patients/${patientId}/sms${params}`).then((res) => res.data)
  },
  getById: (projectId: number, patientId: number): Promise<TPatient> => {
    return http.get(`/projects/${projectId}/patients/${patientId}`).then((res) => res.data)
  },
  postASMSAsPatient: (projectId: number, patientId: number, data: any) => {
    return http.post(`/projects/${projectId}/patients/${patientId}/messenger`, data)
  },
  postImageAsPatient: (projectId: number, patientId: number, file: File) => {
    const formData = new FormData()
    formData.append('image', file)
    return http.post(`/projects/${projectId}/patients/${patientId}/messenger/media`, formData)
  },
  create: (projectId: number, data: any) => {
    return http.post(`/projects/${projectId}/patients`, data).then((res) => res.data)
  },
  update: (projectId: number, patientId: number, data: any): Promise<TPatient> => {
    return http.put(`/projects/${projectId}/patients/${patientId}`, data).then((res) => res.data)
  },
  delete: (projectId: number, patientId: number) => {
    return http.delete(`/projects/${projectId}/patients/${patientId}`).then((res) => res.data)
  },
  deletePatientMedAlerts: (projectId: number, patientId: number, patientMedAlertId: number) => {
    return http.delete(`/projects/${projectId}/patients/${patientId}/patient-med-alerts/${patientMedAlertId}`)
  },
  updatePatientMedEvents: (projectId: number, patientId: number, followupId: number, data: any) => {
    return http
      .post(`/projects/${projectId}/patients/${patientId}/followups/${followupId}/patient-med-events`, data)
      .then((res) => res.data)
  },
  deletePatientMedEvent: (projectId: number, patientId: number, patientMedEventId: number) => {
    return http
      .delete(`/projects/${projectId}/patients/${patientId}/med-events/${patientMedEventId}`)
      .then((res) => res.data)
  },
  getPatientTimeline: (projectId: number, patientId: number) => {
    return http.get(`/projects/${projectId}/patients/${patientId}/timeline`).then((res) => res.data)
  },
  getPatientAllVariables: (projectId: number, patientId: number) => {
    return http
      .get(`/projects/${projectId}/patients/${patientId}/forms/all-variables`)
      .then((res) => res.data)
  },
  /**
   * Download the original interop file for a given patient interop log.
   * The endpoint returns a binary payload with a Content-Disposition filename.
   */
  downloadInteropOriginal: async (
    projectId: number,
    patientId: number,
    interopLogId: number,
  ): Promise<{ blob: Blob; filename?: string }> => {
    const res = await http.get(
      `/projects/${projectId}/patients/${patientId}/interop/${interopLogId}/download-original`,
      {
        responseType: 'blob',
      },
    )
    const disposition = (res.headers as any)?.['content-disposition'] as string | undefined
    let filename: string | undefined
    if (disposition) {
      // Try to extract filename="..." or filename=...
      const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
      filename = decodeURIComponent(match?.[1] || match?.[2] || '') || undefined
    }
    return { blob: res.data as Blob, filename }
  },
  resetPatient: (projectId: number, patientId: number) => {
    return http.put(`/projects/${projectId}/patients/reset/${patientId}`)
  },
  postPatientStartWorkflow: ({
    projectId,
    patientId,
    workflowId,
    testAccountsPool,
    markPatientMedEventsDoneBefore,
    medEventWorkflowRuleId,
    patientMedEventId,
    patientFollowupId,
  }: any) => {
    return http.post(`/projects/${projectId}/patients/${patientId}/start-workflow/${workflowId}`, {
      MedEventWorkflowRuleId: medEventWorkflowRuleId || null,
      PatientMedEventId: patientMedEventId || null,
      PatientFollowupId: patientFollowupId || null,
      testAccountsPool,
      markPatientMedEventsDoneBefore,
    })
  },
  sendConsent: (projectId: number, patientId: number) => {
    return http.put(`/projects/${projectId}/patients/send-consent/${patientId}`)
  },
  postPatientFormsVariable: (projectId: number, patientId: number, patientFormId: number, data: any) => {
    return http
      .post(`/projects/${projectId}/patients/${patientId}/patient-forms/${patientFormId}/variables`, data)
      .then((res) => res.data)
  },
  postDocument: (projectId: number, patientId: number, documentId: number) => {
    return http.post(`/projects/${projectId}/patients/${patientId}/documents`, {
      documentId,
    })
  },
  getDocuments: (projectId: number, patientId: number) => {
    return http.get(`/projects/${projectId}/patients/${patientId}/documents`).then((res) => res.data)
  },
  downloadDocument: (projectId: number, patientId: number, documentId: number) => {
    return http
      .get(`/projects/${projectId}/patients/${patientId}/documents/${documentId}/download-signed-document`, {
        responseType: 'blob',
      })
      .then((res) => res.data)
  },
  downloadPatientReport: (projectId: number, patientId: number, patientFollowupId: number) => {
    return http
      .get(`/projects/${projectId}/patients/${patientId}/report/${patientFollowupId}`, {
        responseType: 'blob',
      })
      .then((res) => res.data)
  },
  getPatientComments: (projectId: number, patientId: number): Promise<TMedicalTeamComment[]> => {
    return http.get(`/projects/${projectId}/patients/${patientId}/comments`).then((res) => res.data)
  },
  postPatientComment: (projectId: number, patientId: number, data: any) => {
    return http.post(`/projects/${projectId}/patients/${patientId}/comments`, data).then((res) => res.data)
  },
  putPatientComment: (projectId: number, patientId: number, id: number, data: any) => {
    return http
      .put(`/projects/${projectId}/patients/${patientId}/comments/${id}`, data)
      .then((res) => res.data)
  },
  deletePatientComment: (projectId: number, patientId: number, id: number) => {
    return http.delete(`/projects/${projectId}/patients/${patientId}/comments/${id}`)
  },
  getPatientAuditData: (projectId: number, patientId: number) => {
    return http.get(`/projects/${projectId}/patients/${patientId}/audit`)
  },
  getPatientResourceChangeLogs: (
    projectId: number,
    patientId: number,
    filters?: TResourceChangeLogFilters,
  ): Promise<TResourceChangeLog[]> => {
    const params = new URLSearchParams()
    if (filters?.startDate) {
      params.append('startDate', filters.startDate)
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate)
    }
    if (filters?.fieldNames) {
      filters.fieldNames.forEach((fieldName) => params.append('fieldNames[]', fieldName))
    }
    if (filters?.modelName) {
      params.append('modelName', filters.modelName)
    }
    if (filters?.recordId) {
      params.append('recordId', filters.recordId)
    }
    const queryString = params.toString() ? `?${params.toString()}` : ''
    const url = `/projects/${projectId}/patients-backoffice/${patientId}/resource-change-logs${queryString}`
    return http.get(url).then((res) => res.data)
  },
  endFollowup: ({
    projectId,
    patientId,
    patientFollowupId,
    data,
  }: {
    projectId: number
    patientId: number
    patientFollowupId: number
    data: any
  }) => {
    return http.post(`/projects/${projectId}/patients/${patientId}/followups/${patientFollowupId}/stop`, data)
  },
}
